import { useTranslation } from "react-i18next";
import { Building2, ChevronRight } from "lucide-react";
import comminovaLogo from "@/assets/logos/Comminova-logo01.svg";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/use-logout";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import type { RestaurantMembership } from "@/stores/restaurantStore";
import { getDisplayInitial } from "@/utils/displayInitial";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

type RestaurantPickerProps = {
  memberships: RestaurantMembership[];
  onSelect: (m: RestaurantMembership) => void;
};

export function RestaurantPicker({
  memberships,
  onSelect,
}: RestaurantPickerProps) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const logout = useLogout();

  const roleLabelMap: Record<string, string> = {
    owner: t("auth.roles.owner"),
    admin: t("auth.roles.admin"),
    manager: t("auth.roles.manager"),
    cashier: t("auth.roles.cashier"),
    kitchen: t("auth.roles.kitchen"),
    staff: t("auth.roles.staff"),
  };

  function getRoleBadgeVariant(role: string): "default" | "secondary" | "outline" {
    if (role === "owner") return "default";
    if (role === "admin" || role === "manager") return "secondary";
    return "outline";
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-secondary to-white p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center">
        <div className="mb-5">
          <img
            src={comminovaLogo}
            alt="Comminova"
            className="h-7 w-auto object-contain"
          />
        </div>

        <Card className="w-full max-w-md rounded-xl border border-border shadow-md">
          <div className="space-y-4 px-6 pt-6 pb-2">
            <div className="text-center space-y-1.5">
              <h1 className="text-xl font-semibold">
                {t("auth.pickRestaurantTitle")}
              </h1>
            </div>
            <Separator />
          </div>

          {memberships.length === 0 ? (
            <div className="px-6 py-6">
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">{t("auth.noRestaurantsFound")}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("auth.noRestaurantsBody")}
                </p>
                <a
                  href="mailto:info@comminova.se"
                  className="mt-3 inline-block text-xs text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                >
                  {t("auth.contactSupport")}
                </a>
              </div>
            </div>
          ) : (
            <ul className="pb-6">
              {memberships.map((m, idx) => {
                const restaurantName = m.restaurantName || m.slug || m.restaurantId;
                const roleLabel = roleLabelMap[m.role] ?? m.role;
                const showDivider = idx < memberships.length - 1;

                return (
                  <li key={m.id} className={showDivider ? "border-b border-border" : ""}>
                    <button
                      type="button"
                      className="group flex w-full transform-gpu cursor-pointer items-center gap-3 px-6 py-3.5 text-left transition-all duration-150 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]"
                      onClick={() => onSelect(m)}
                    >
                      <Avatar className="h-9 w-9">
                        {m.logoUrl ? (
                          <AvatarImage src={m.logoUrl} alt={restaurantName} />
                        ) : null}
                        <AvatarFallback className="bg-muted text-sm font-medium text-foreground">
                          {getDisplayInitial(m.restaurantName, m.slug, m.restaurantId)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{restaurantName}</p>
                        <Badge
                          variant={getRoleBadgeVariant(m.role)}
                          className="mt-1 h-5 px-1.5 text-xs capitalize"
                        >
                          {roleLabel}
                        </Badge>
                      </div>

                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          <p>
            {t("auth.loggedInAs", { email: user?.email ?? t("auth.unknownUser") })}{" "}
            <button
              type="button"
              onClick={() => void logout()}
              className="underline underline-offset-4 transition-colors hover:text-foreground"
            >
              {t("nav.logout")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
